import { z } from "zod";

/**
 * The read half of IMS's read/write schema policy.
 *
 * **The policy, in one paragraph.** A schema that validates a payload coming
 * *out* of the API and a schema that validates a payload going *in* are two
 * different contracts, and sharing one object for both is what produced three
 * live defects on the five entities that round-trip `PUT /{x}/enrich`:
 *
 * 1. a strict `z.object` strips keys it does not model, and the PUT is a full
 *    replace — so pipeline-owned keys (`taskName`, `enrichmentSources`) were
 *    destroyed on every curator "Approve & Save";
 * 2. a field the *submit* contract needs (a lifecycle status) became a freely
 *    editable AutoForm field, letting a curator write a state no enricher ran;
 * 3. a hard `.parse()` on read throws on one unexpected field and blanks the
 *    entire edit page.
 *
 * So: **read schemas are lenient** — every field `.nullish()`, every object
 * `.passthrough()`, consumed through {@link parseRead} — and **submit schemas
 * are strict**, validating what the endpoint actually accepts (and also
 * `.passthrough()`, so a key IMS does not model still survives the round-trip).
 *
 * `src/lib/zod/show-fragments.ts` already documented and used this for the four
 * read-only entities (movie, person, screening, show_genre); `use-show-record.ts`
 * is the same safeParse-with-raw-fallback. This module is that policy made
 * importable so the enrichment entities do not each reinvent it.
 */

/**
 * `safeParse` with a raw fallback: on a parse miss, log the issues and hand the
 * caller the untouched payload rather than throwing.
 *
 * A backend field rename, a value outside an enum, a newly-nullable column —
 * none of those should blank a page whose 40 other fields arrived fine. The
 * cast is deliberate and is the whole point: rendering slightly-wrong data
 * beats rendering nothing, and the console entry is what tells us to fix the
 * schema.
 *
 * @param schema the lenient READ schema for this payload
 * @param raw    whatever the data provider returned
 * @param label  a short identifier for the log line, e.g. `"artist"`
 */
export function parseRead<S extends z.ZodTypeAny>(
  schema: S,
  raw: unknown,
  label: string,
): z.infer<S> {
  const parsed = schema.safeParse(raw);
  if (parsed.success) return parsed.data as z.infer<S>;
  // eslint-disable-next-line no-console
  console.error(`[read-schema] ${label} payload did not match`, parsed.error);
  return raw as z.infer<S>;
}
